import React, {useCallback} from 'react';
import {
  TypeImplementation,
  Plugins,
  getSystem,
  withSystem,
  EnvTypes,
  withMeta,
} from '@packages/systems/wfdl';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  TBoolean,
  TRUE,
  toBooleanTag,
  BooleanTag,
} from '@packages/systems/plugins-utils/Boolean';
import {
  EBoolean,
  EList,
  Expression,
  TList,
  ECase,
  TBoolean as TBuiltinBoolean,
} from '@packages/systems/wfdl-ast';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  updateAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionBinding,
  ExpressionBindMetaPayload,
  updateExpressionAction,
} from '@packages/systems/ExpressionEditor-utils';
import {VisibilityConditionsType} from './constants';
import {tyfun, func, $} from '@packages/utilities/wfdl-helpers';
import {
  SegmentedControl,
  SegmentedControlButton,
} from '@packages/systems/spring';

const VisibilityConditionsRenderer = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  // Update the ContextualExpression that is passed to ExpressionBinding
  // to be a `VisibilityConditions` one, since the Alias render has the
  // ContextualExpression type as the unwrapped Alias type List<Visibilty>
  const newExpr = updateAnn((ann) => {
    const type = VisibilityConditionsType;

    if (!ann.type) {
      return ann;
    }

    const system = getSystem(ann.type);
    return {
      type: system ? withSystem(system, type) : type,

      env: ann.env,

      errors: ann.errors,
    };
  }, expr);

  return (
    <SimpleVisibilityConditionsRender expr={newExpr} dispatch={dispatch} />
  );
};

function SimpleVisibilityConditionsRender({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) {
  // We expect conditions to be an array of booleans. For the use case of show/hide
  // the array contains a single boolean value that indicates simply if an element is visible or not
  const conditionsList = ContextualExpressions.getList(stripAnn(expr));
  const boolExprList = conditionsList ? conditionsList.map(stripAnn) : [];

  // While we have a simplified visible/hidden UI, we expect to have at most
  // one item in `conditionList`, therefore it's enough to check for the first
  // element if it exists.
  const conditionResult = boolExprList.length ? boolExprList[0] : null;
  const conditionValue = conditionResult
    ? ContextualExpressions.getBoolean(conditionResult)
    : null;

  // visible/hidden value that can be undefined (for example when the value is bound or a hole)
  const maybeValue =
    typeof conditionValue !== 'boolean'
      ? undefined
      : conditionValue
        ? 'visible'
        : 'hidden';

  // ensures that potentially holey values can be changed
  const onChange = useCallback(
    (newVal: string) => {
      if (newVal !== maybeValue) {
        const newExpr = EList([EBoolean(newVal === 'visible')]);
        dispatch(
          updateExpressionAction(newExpr, {
            analytics: {type: 'visibility-changed', value: newVal},
          })
        );
      }
    },
    [dispatch, maybeValue]
  );

  const onBindingChange = React.useCallback(
    (updatedBinding: null | Expression, meta: ExpressionBindMetaPayload) => {
      dispatch({
        type: 'EXPRESSION_BIND_CHANGED',
        binding: updatedBinding,
        meta,
        key: [],
      });
    },
    [dispatch]
  );

  return (
    <ExpressionBinding
      expression={expr}
      onChange={onBindingChange}
      bindUIVariant="dot"
      automationLabel="Visibility"
    >
      <SegmentedControl onChange={onChange} value={maybeValue ?? 'visible'}>
        <SegmentedControlButton value="visible">Visible</SegmentedControlButton>
        <SegmentedControlButton value="hidden">Hidden</SegmentedControlButton>
      </SegmentedControl>
    </ExpressionBinding>
  );
}

export const VisibilityConditions = TypeImplementation.Alias({
  type: TList(withSystem({isBindable: false}, TBuiltinBoolean)),
  render: VisibilityConditionsRenderer,
  system: {
    label: 'Visibility',
    displayName: 'Visibility',
    bindUIVariant: 'custom',
  },
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('Boolean'))
  ),
  renderSummary: ({expr}: {expr: ContextualExpression}) => {
    // Only show renderSummary of the first item
    const list = ContextualExpressions.getList(stripAnn(expr));
    const firstItem = list ? list[0] : null;
    const firstItemBoolean = firstItem
      ? ContextualExpressions.getBoolean(stripAnn(firstItem))
      : null;
    if (firstItemBoolean !== null) {
      const label = firstItemBoolean ? 'Visible' : 'Hidden';
      return <span>{label}</span>;
    }
    return null;
  },
});

export const booleanToVisibilityConditions: EnvTypes.SafeTypedValue = {
  envType: {
    dataType: tyfun(
      [
        withSystem(
          {label: 'Visibility'},
          withMeta({trueLabel: 'Visible', falseLabel: 'Hidden'}, TBoolean)
        ),
      ],
      VisibilityConditionsType
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  expression: func(
    ['boolean value'],
    ECase({
      value: $('boolean value'),
      cases: {
        True: func(['_'], EList([EBoolean(true)])),
        False: func(['_'], EList([EBoolean(false)])),
      },
    })
  ),
};

export const visibilityConditionsToBoolean: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: tyfun([VisibilityConditionsType], TBoolean),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (visibilityConditions: unknown): BooleanTag => {
    if (
      Array.isArray(visibilityConditions) &&
      visibilityConditions.length > 0
    ) {
      return toBooleanTag(visibilityConditions.every(Boolean));
    }
    return TRUE;
  },
};
